// NPM
import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Stack } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';

// MESSAGES
import BillingFormattedMessages from '../../messages/billing/BillingFormattedMessages';

// ROUTES
import { BillingRoutes } from '../../routes/BillingRoutes';

// NETWORKING
import useInvoiceRequest from '../../requests/InvoiceRequest';

// CONFIG
import Config from '../../Config';

// UTILS
import { buildHash, parseHashFromProps } from '../../utils/pagination';
import { getCurrencyType } from '../../utils/invoicePresenter';

// COMPONENTS
import Layout from '../../components/layouts/Layout';
import InvoiceTable from '../../components/billing/invoices/InvoiceTable';
import InvoiceTableRow from '../../components/billing/invoices/InvoiceTableRow';
import InvoiceAdjustment from '../../components/billing/invoices/InvoiceAdjustments';
import StagedModal from '../../components/common/StagedModal';
import InvoiceFilters from '../../components/billing/invoices/InvoiceFilters';
import Loader from '../../components/common/Loader';
import Paginator from '../../components/common/Paginator';

const Invoice = ({
  errorHandler,
  updateActingCustomerDetails,
  accessLevel,
  accessType,
  customer,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    searchInvoices,
    processInvoiceTransaction,
    updateStatus,
    emailInvoice,
    refreshInvoice,
  } = useInvoiceRequest();

  const { page } = parseHashFromProps({ location });
  const defaultFilters = {
    providerFilter: '',
    customerFilter: location.state?.customerName || '',
    invoiceFilter: '',
    statusFilter: '',
    currencyFilter: '',
    customerTypeFilter: '',
  };

  const [stv, setStv] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [isSelected, setIsSelected] = useState(false);
  const [singleBill, setSingleBill] = useState(null);
  const [showStaged, setShowStaged] = useState(false);
  const [timeRange, setTimeRange] = useState(null);
  const [stage, setStage] = useState(0);
  const [refreshPage, setRefreshPage] = useState(false);
  const [modalParameters, setModalParameters] = useState({});
  const [modalActionType, setModalActionType] = useState(null);
  const [currentlySearching, setCurrentlySearching] = useState({});
  const [selectorKeyRefresh, setSelectorKeyRefresh] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [refreshedBill, setRefreshedBill] = useState(null);
  const [redirectToInvoice, setRedirectToInvoice] = useState(false);

  const [filters, setFilters] = useState(defaultFilters);
  const [filterChangeTimeout, setFilterChangeTimeout] = useState(null);

  const [rowCount, setRowCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(page ?? 1);
  const [pageLength, setPageLength] = useState(Config.defaultPL);

  const getData = useCallback(() => {
    if (fromDate && toDate) handleUserDataSearch(fromDate, toDate, timeRange);
  }, [filters, fromDate, toDate, timeRange, pageNumber]);

  useEffect(() => {
    getData();
  }, [getData]);

  const setHash = () => {
    const currentHash = location.hash;
    const currentlyEmpty = !currentHash || currentHash === '#';

    const hash = buildHash(pageNumber);

    if (currentlyEmpty && hash === '') {
      return;
    }

    window.location.hash = hash;
  };

  const requestData = async (fromDate, toDate, timeRange) => {
    setHash();

    const searchParams = {};
    searchParams.fromDate = fromDate;
    searchParams.toDate = toDate;
    searchParams.timeRange = timeRange;
    searchParams.providerFilter = filters.providerFilter;
    searchParams.invoiceFilter = filters.invoiceFilter;
    searchParams.customerFilter = filters.customerFilter;
    searchParams.currencyFilter = filters.currencyFilter;
    searchParams.customerTypeFilter = filters.customerTypeFilter;
    searchParams.statusFilter = filters.statusFilter;
    searchParams.pageNumber = pageNumber;
    searchParams.pageLength = pageLength;

    console.log(searchParams);

    const response = await searchInvoices(searchParams, errorHandler);

    return {
      data: response.items || [],
      rowCount: response.rowCount,
      currentlySearching: {
        providerFilter: searchParams.providerFilter,
        invoiceFilter: searchParams.invoiceFilter,
        customerFilter: searchParams.customerFilter,
        statusFilter: searchParams.statusFilter,
        currencyFilter: searchParams.currencyFilter,
        customerTypeFilter: searchParams.customerTypeFilter,
      },
    };
  };

  const performAction = async (invoiceId, customerId, providerId, billId) => {
    switch (modalActionType) {
      case 'processPendingInvoice':
        console.log('processPendingInvoice');
        await processPendingInvoice(billId, customerId);
        break;
      case 'transitionPendingToInvoiced':
        console.log('transitionPendingToInvoiced');
        await transitionPendingToInvoiced(customerId, providerId, billId);
        break;
      case 'transitionBillToFailed':
        console.log('transitionBillToFailed');
        await transitionBillToFailed(customerId, providerId, billId);
        break;
      case 'cancelPendingTransaction':
        console.log('cancelPendingTransaction');
        await cancelPendingTransaction(customerId, providerId, billId);
        break;
      case 'emailInvoice':
        console.log('emailInvoice');
        await emailRequestHandler(customerId, providerId, billId);
        break;
      case 'refreshInvoice':
        console.log('refreshRequestHandler');
        await refreshRequestHandler(customerId, providerId, billId);
        break;
      default:
        console.error('Invoice attempted to perform an unknown action');
        break;
    }
  };

  const processPendingInvoice = async (billId, customerId) => {
    if (!showStaged) {
      setModalParameters({
        billId,
        customerId,
        stageTitle: 'Process Transaction',
        stageConfirmation:
          'Are you sure you would like to process payment on this invoice?',
        stagePending: 'Processing transaction...',
        stageSuccess: 'Invoice paid successfully!',
        stageFailure: 'Invoice payment failed!',
      });
      setModalActionType('processPendingInvoice');
      setShowStaged(true);
      return;
    }

    if (stage !== 0) {
      return;
    }

    setStage(1);

    const results = await processInvoiceTransaction(
      modalParameters?.customerId,
      modalParameters?.billId,
      errorHandler
    );
    if (results.message === 'Successful Payment') {
      setStage(2);
      return;
    }
    setModalParameters({
      stageTitle: 'Invoice Bill',
      errorMsg: results.message,
      stageFailure: 'Invoice payment failed!',
    });
    setStage(3);

    dataChange(results, results.paid_date);
  };

  const transitionPendingToInvoiced = async (
    customerId,
    providerId,
    billId
  ) => {
    if (!showStaged) {
      setModalParameters({
        billId,
        customerId,
        providerId,
        stageTitle: 'Invoice Bill',
        stageConfirmation:
          'Are you sure you would like convert this pending bill to invoiced?',
        stagePending: 'invoicing bill...',
        stageSuccess: 'Bill Invoiced successfully!',
        stageFailure: 'Invoice Generation failed!',
      });
      setModalActionType('transitionPendingToInvoiced');
      setShowStaged(true);
    } else if (stage === 0) {
      setStage(1);
      let result = await updateStatus(
        modalParameters?.providerId,
        modalParameters?.customerId,
        modalParameters?.billId,
        'I', // Invoiced
        errorHandler
      );
      console.log('got results updating invoice:', result);
      if (result.status === 'Invoiced') setStage(2);
      else setStage(3);
      dataChange(result);
    }
  };

  const transitionBillToFailed = async (customerId, providerId, billId) => {
    if (!showStaged) {
      setModalParameters({
        billId,
        customerId,
        providerId,
        stageTitle: 'Fail Transaction',
        stageConfirmation: 'Are you sure you want to fail this invoice?',
        stagePending: 'Failing Invoice...',
        stageSuccess: 'Invoice successfully transitioned to "Failed"!',
        stageFailure: 'Invoice failed to transition to "Failed"!',
      });
      setModalActionType('transitionBillToFailed');
      setShowStaged(true);
    } else if (stage === 0) {
      setStage(1);
      let result = await updateStatus(
        modalParameters?.providerId,
        modalParameters?.customerId,
        modalParameters?.billId,
        'F', //Failed
        errorHandler
      );
      console.log('got results updating invoice:', result);
      if (result.status === 'Failed') setStage(2);
      else setStage(3);
      dataChange(result);
    }
  };

  const cancelPendingTransaction = async (customerId, providerId, billId) => {
    if (!showStaged) {
      setModalParameters({
        billId,
        customerId,
        providerId,
        stageTitle: 'Cancel Transaction',
        stageConfirmation: 'Are you sure you want to cancel this invoice?',
        stagePending: 'Canceling Invoice...',
        stageSuccess: 'Invoice Cancelled successfully!',
        stageFailure: 'Invoice failed to be cancelled!',
      });
      setModalActionType('cancelPendingTransaction');
      setShowStaged(true);
    } else if (stage === 0) {
      setStage(1);
      let result = await updateStatus(
        modalParameters?.providerId,
        modalParameters?.customerId,
        modalParameters?.billId,
        'C', // Cancelled
        errorHandler
      );
      console.log('got results updating invoice:', result);
      if (result.status === 'Cancelled') setStage(2);
      else setStage(3);
      dataChange(result);
    }
  };

  const removeStaged = async (refreshPage, redirectToInvoice) => {
    setShowStaged(false);
    setModalParameters({});

    if (refreshPage) {
      setLoading(true);

      const response = await requestData(fromDate, toDate, timeRange);

      setData(response.items);
      setRefreshPage(false);
      setRowCount(response.rowCount);
      formatData(response.items);
    }

    if (redirectToInvoice) {
      const redirectUrl = `/billing/invoiceshow/${refreshedBill.customer_id}/${refreshedBill.bill_id}`;
      navigate(redirectUrl);
      setRedirectToInvoice(false);
    }
  };

  const emailRequestHandler = async (providerId, customerId, billId) => {
    if (!showStaged) {
      setModalParameters({
        billId,
        customerId,
        providerId,
        stageTitle: 'Email Invoice',
        stageConfirmation: 'Are you sure you want to Email this invoice?',
        stagePending: 'Emailing Invoice...',
        stageSuccess: 'Invoice Sent successfully!',
        stageFailure: 'Invoice send failed!',
      });
      setModalActionType('emailInvoice');
      setShowStaged(true);

      return;
    }

    if (stage !== 0) {
      return;
    }

    setStage(1);

    const response = await emailInvoice(
      modalParameters?.providerId,
      modalParameters?.customerId,
      modalParameters?.billId,
      errorHandler
    );
    if (response) {
      setModalParameters({
        ...modalParameters,
        stageSuccess:
          'Invoice Sent successfully to:\n' + response.contacts?.join(', \n'),
      });
      setStage(2);

      return;
    }
    setStage(3);
  };

  const printRequestHandler = (id) => {
    navigate('/billing/invoiceshow/' + id + '?print=true');
  };

  const adjustRequestHandler = (element) => {
    console.log('billId', element.bill_id);
    setIsSelected(true);
    setSingleBill(element);
  };

  const backButtonClick = async (refresh) => {
    let defaultState = {
      isSelected: false,
      singleBill: null,
    };

    if (refresh) {
      const response = await requestData(fromDate, toDate, timeRange);
      defaultState.data = response.items;
      defaultState.rowCount = response.rowCount;
    }
    setData(defaultState.data);
    setRowCount(defaultState.rowCount);
    setIsSelected(defaultState.isSelected);
    setSingleBill(defaultState.singleBill);
    formatData(defaultState.data);
  };

  const refreshRequestHandler = async (providerId, customerId, billId) => {
    if (!showStaged) {
      setModalParameters({
        billId,
        customerId,
        providerId,
        stageTitle: 'Refresh Invoice',
        stageConfirmation: 'Are you sure you want to refresh this invoice?',
        stagePending: 'Refreshing Invoice...',
        stageSuccess: 'Invoice refreshed successfully!',
        stageFailure: 'Invoice refresh failed!',
      });
      setModalActionType('refreshInvoice');
      setShowStaged(true);
    } else if (stage === 0) {
      setStage(1);

      let response = await refreshInvoice(
        modalParameters?.billId,
        modalParameters?.customerId,
        errorHandler
      );
      console.log(`response in index.js:`, response);
      if (response) {
        setStage(2);
        setRedirectToInvoice(true);
        setRefreshedBill(response);
      } else {
        setStage(3);
        console.log('error with refresh, got no response');
      }
    }
  };

  const dataChange = async (result, updatePaidDate) => {
    if (!result) return;

    let data = [...data];
    data.forEach((element) => {
      if (element.invoice_number === result.invoice) {
        element.billing_status = result.status;
        if (updatePaidDate) {
          element.paid_date = result.paid_date;
        }
      }
    });
    setData(data);
    formatData(data);
  };

  const stvCallback = () => {
    setStv(false);
  };

  const handleUserDataSearch = async (fromDate, toDate, timeRange) => {
    console.log(
      'handleUserDataSearch',
      filters.providerFilter,
      filters.customerFilter
    );
    try {
      setLoading(true);
      setData([]);

      const stateFields = await requestData(fromDate, toDate, timeRange);

      setData(stateFields.data);
      setRowCount(stateFields.rowCount);
      setLoading(false);
      setPageLoaded(true);
      setCurrentlySearching(stateFields.currentlySearching);
      formatData(stateFields.data);
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  };

  const toggleMenu = () => setStv((prev) => !prev);

  const handleInputChange = (event) => {
    const target = event.target;
    const name = target.name;
    const value = target.type === 'checkbox' ? target.checked : target.value;

    console.log('handleInputChange event', event);
    setFilters({
      ...filters,
      [name]: value,
    });

    if (filterChangeTimeout) {
      clearTimeout(filterChangeTimeout);
    }

    setFilterChangeTimeout(
      setTimeout(() => {
        setFilterChangeTimeout(null);
        setPageNumber(1);
        setLoading(true);
      }, 300)
    );
  };

  const clearFilters = () => {
    setFilters(defaultFilters);
    document.getElementById('filter-form').reset();
  };

  const selectCustomer = async (customerId) => {
    await updateActingCustomerDetails(customerId);
    navigate('/admin/customers/' + customerId);
  };

  const formatData = async (rawData) => {
    try {
      if (!rawData || rawData.length === 0) {
        setData([]);
        setLoading(false);
        return false;
      }

      const formattedData = rawData.map((el) => {
        return (
          <InvoiceTableRow
            id={el.provider_id}
            key={el.invoice_number} // was originally set to provider_id, but why? resulted in many warnings.
            provider={el.provider_name}
            billId={el.bill_id}
            invoiceId={el.invoice_number}
            customerId={el.customer_id}
            name={el.customer_name}
            invoiceDate={el.invoice_date}
            paidDate={el.paid_date}
            status={el.billing_status}
            currency={getCurrencyType(el)}
            dollar={el.plan_currency}
            customerType={el.customer_type || customer.customer_type}
            amount={el.amount}
            isGodAdmin={accessLevel === '3' && accessType === '3'}
            emailRequestHandler={() =>
              emailRequestHandler(el.provider_id, el.customer_id, el.bill_id)
            }
            printRequestHandler={printRequestHandler}
            refreshRequestHandler={() =>
              refreshRequestHandler(el.provider_id, el.customer_id, el.bill_id)
            }
            adjustRequestHandler={() => adjustRequestHandler(el)}
            errorHandler={errorHandler}
            processPendingInvoice={processPendingInvoice}
            transitionPendingToInvoiced={transitionPendingToInvoiced}
            transitionBillToFailed={transitionBillToFailed}
            cancelPendingTransaction={cancelPendingTransaction}
            dataChange={dataChange}
            selectCustomer={selectCustomer}
          />
        );
      });

      setData(formattedData);
      setLoading(false);
    } catch (e) {
      console.log('data error:', e);
    }
  };

  const changePageEvent = (page) => {
    setPageNumber(page);
  };

  const changePageLength = async (e) => {
    if (e.target) {
      const value = e.target.value;
      setPageLength(value);
      handleUserDataSearch(fromDate, toDate, timeRange);
    }
  };

  if (accessLevel <= '0' || accessType === '0') {
    return null;
  }

  return (
    <Layout
      routes={BillingRoutes}
      title={
        isSelected
          ? BillingFormattedMessages.createInvoiceAdjustment
          : BillingFormattedMessages.invoicesText
      }
      accessLevel={accessLevel}
      accessType={accessType}
      customerType={customer?.customer_type}
      backgroundColor="none"
      handleBackButton={isSelected && backButtonClick}
      backButtonText={isSelected && BillingFormattedMessages.backText}
    >
      {isSelected ? (
        <InvoiceAdjustment
          backButton={backButtonClick}
          bill={singleBill}
          errorHandler={errorHandler}
        />
      ) : (
        <Stack>
          <InvoiceFilters
            clearFilters={clearFilters}
            handleInputChange={(e) => handleInputChange(e)}
            accessLevel={accessLevel}
            accessType={accessType}
            handleUserDataSearch={(fromDate, toDate, timeRange) =>
              handleUserDataSearch(fromDate, toDate, timeRange)
            }
            statusFilterDropdown={filters.statusFilter}
            customerTypeFilterDropdown={filters.customerTypeFilter}
            currentlySearching={currentlySearching}
            selectorKeyRefresh={selectorKeyRefresh}
            fromDate={fromDate}
            setFromDate={setFromDate}
            toDate={toDate}
            setToDate={setToDate}
            filters={filters}
          />

          <Stack sx={{ gap: 4 }}>
            {loading ? (
              <Loader />
            ) : data && data.length > 0 ? (
              <>
                <InvoiceTable
                  list={data}
                  isGodAdmin={accessLevel === '3' && accessType === '3'}
                />

                <Paginator
                  pageNumber={pageNumber}
                  setPageNumber={setPageNumber}
                  itemCount={rowCount ?? 0}
                  changeEvent={changePageEvent}
                />
              </>
            ) : (
              <Typography>{BillingFormattedMessages.noResultsFound}</Typography>
            )}
          </Stack>
        </Stack>
      )}

      <StagedModal
        showStaged={showStaged}
        removeStaged={() => removeStaged(refreshPage, redirectToInvoice)}
        removeStagedSlow={() => setStage(0)}
        stage={stage}
        performAction={performAction}
        stageTitle={modalParameters?.stageTitle}
        stageConfirmation={modalParameters?.stageConfirmation}
        stagePending={modalParameters?.stagePending}
        stageSuccess={modalParameters?.stageSuccess}
        stageFailure={
          modalParameters?.stageFailure +
          (modalParameters?.errorMsg ? ': ' + modalParameters.errorMsg : '')
        }
      />
    </Layout>
  );
};

export default Invoice;
